// ─── EDIT THIS FILE TO CUSTOMIZE YOUR CONTENT ───────────────────────────────

export const HERO = {
  eyebrow: "",
  title: "Martech Architecture & Solutions",
  subtitle: "Connecting data, technology, and strategy to drive measurable marketing performance.",
  // Replace with your actual photo path in /public folder
  photo: "/assets/xbc-x.png",
  photoPlaceholder: false, // Set to false once you add a real photo
};

export const CONTACT = {
  email: "contact@xbcsolutions.com",
};

export const COMPANIES = [
  { name: "SP Global", logo: "/assets/logos/spglobal2.png" },
  { name: "Affinity", logo: "/assets/logos/affinity petcare-modified.png" },
  { name: "Bankia", logo: "/assets/logos/caixa_bankia-modified.jpg" },
  { name: "Mapfre", logo: "/assets/logos/mapfre logo-modified.png" },
  { name: "Merkle", logo: "/assets/logos/merkle logo-modified.jpg" },
  { name: "Paper & Spark", logo: "/assets/logos/paper-spark-modified.png" },
];

export const SERVICES = [
  {
    id: "martech-stack-design",
    title: "Martech Stack Design",
    icon: "◈",
    summary: "End-to-end architecture of your marketing technology ecosystem — selecting, integrating, and optimizing the right tools for your business goals.",
    detail: {
      intro: "A well-designed martech stack is the foundation of scalable, data-driven marketing. We work with you to audit your current tools, identify gaps, and architect a cohesive ecosystem that eliminates redundancy and maximizes ROI.",
      highlights: [
        "Current stack audit and gap analysis",
        "Vendor selection and RFP management",
        "Integration architecture and data flow design",
        "Roadmap planning and phased rollout",
        "Team training and change management",
      ],
      outcome: "Clients typically see 30–50% reduction in tool sprawl and significant improvement in cross-channel data consistency within the first 6 months.",
    },
  },
  {
    id: "cdp-data-strategy",
    title: "CDP & Data Strategy",
    icon: "◉",
    summary: "Designing and implementing Customer Data Platforms that unify first-party data, enable personalization at scale, and future-proof your data strategy.",
    detail: {
      intro: "First-party data is the most valuable asset in modern marketing. We help organizations implement CDPs that create a single customer view, enabling real-time personalization, advanced segmentation, and compliant data activation.",
      highlights: [
        "CDP vendor evaluation (Segment, mParticle, Tealium, etc.)",
        "Identity resolution strategy",
        "Data taxonomy and schema design",
        "Activation and audience strategy",
        "Consent management and privacy compliance",
      ],
      outcome: "Typical outcomes include 2–4x improvement in audience match rates and the ability to deliver truly personalized experiences across all customer touchpoints.",
    },
  },
  {
    id: "marketing-automation",
    title: "Marketing Automation",
    icon: "◎",
    summary: "Designing sophisticated automation workflows and lead nurturing programs that scale your pipeline and deliver the right message at the right moment.",
    detail: {
      intro: "Marketing automation is only as effective as the strategy behind it. We design and implement automation programs that align with the customer journey, from initial awareness through post-purchase retention, leveraging behavioral triggers and predictive signals.",
      highlights: [
        "Journey mapping and automation design",
        "Lead scoring model development",
        "Multi-channel campaign orchestration",
        "CRM and MAP integration (Salesforce, Marketo, HubSpot)",
        "Performance measurement and optimization frameworks",
      ],
      outcome: "Clients have achieved up to 60% increase in MQL-to-SQL conversion rates and significant reductions in sales cycle length through well-structured automation.",
    },
  },
  {
    id: "analytics-attribution",
    title: "Analytics & Attribution",
    icon: "◐",
    summary: "Building measurement frameworks and multi-touch attribution models that give you true visibility into marketing performance and ROAS across all channels.",
    detail: {
      intro: "Without accurate measurement, optimization is guesswork. We design robust analytics architectures — from tracking implementation to attribution modeling and executive dashboards — that give your team confidence in every marketing decision.",
      highlights: [
        "GA4 and server-side tracking implementation",
        "Multi-touch attribution modeling",
        "Marketing mix modeling (MMM)",
        "Custom dashboard development (Looker, Tableau, Power BI)",
        "Incrementality testing and experimentation frameworks",
      ],
      outcome: "Organizations gain clear visibility into true marketing ROI, enabling them to reallocate budget to highest-performing channels and justify marketing investment at the board level.",
    },
  },
];

export const CASE_STUDIES = [
  {
    id: "retail-cdp-unification",
    tag: "CDP Implementation",
    title: "Unifying 4M Customer Profiles for a European Retailer",
    summary: "Architected a CDP implementation that unified fragmented data across 12 systems, enabling real-time personalization and a 38% lift in email revenue.",
    detail: {
      client: "Major European Retail Chain (confidential)",
      challenge: "The client had 12+ disconnected data sources, no single customer view, and was losing revenue to generic, untargeted campaigns. Their marketing team was working in silos with incompatible tools.",
      solution: "We led the architecture and implementation of a Segment CDP, designing a custom identity resolution strategy that stitched online and offline behavior. We built a data layer that ingested POS, e-commerce, loyalty, and behavioral data in real time.",
      results: [
        "4.2 million customer profiles unified from 12 data sources",
        "38% increase in email campaign revenue within 6 months",
        "Real-time personalization deployed across web, email, and app",
        "60% reduction in time-to-segment for the marketing team",
      ],
      duration: "9 months",
      tools: ["Segment", "Salesforce Marketing Cloud", "BigQuery", "Looker"],
    },
  },
  {
    id: "b2b-automation-scale",
    tag: "Marketing Automation",
    title: "Scaling B2B Pipeline for a SaaS Company",
    summary: "Redesigned the entire lead lifecycle and automation strategy, resulting in a 2.5x increase in qualified pipeline and 45% shorter sales cycles.",
    detail: {
      client: "Series B SaaS Company — HR Tech sector",
      challenge: "The company had outgrown its basic automation setup. MQL volume was high but quality was poor, leading to friction with sales. There was no behavioral scoring, and nurture sequences were static and generic.",
      solution: "We audited and redesigned the entire lead lifecycle — from initial capture through customer onboarding. This included building a predictive lead scoring model using firmographic, demographic, and behavioral signals, and creating 18 distinct nurture journeys mapped to persona and buying stage.",
      results: [
        "MQL-to-SQL conversion rate improved from 8% to 22%",
        "Average sales cycle reduced from 67 to 37 days",
        "Pipeline value increased 2.5x without increasing ad spend",
        "Sales satisfaction with lead quality jumped from 31% to 78%",
      ],
      duration: "6 months",
      tools: ["HubSpot", "Clearbit", "Salesforce CRM", "Tableau"],
    },
  },
  {
    id: "attribution-overhaul",
    tag: "Analytics & Attribution",
    title: "Building a Multi-Touch Attribution Model for a Finance Brand",
    summary: "Replaced last-click attribution with a data-driven MMM model, revealing true channel performance and enabling a budget reallocation that drove 22% more revenue.",
    detail: {
      client: "Financial Services Brand — Wealth Management",
      challenge: "All marketing decisions were based on last-click attribution, which was dramatically over-crediting paid search and under-valuing brand and awareness channels. Budget allocation was inefficient and the CMO lacked confidence in reported ROAS.",
      solution: "We designed and implemented a multi-touch attribution framework using a combination of data-driven attribution (via GA4) and a Marketing Mix Model built in Python. The MMM was validated against holdout tests to ensure accuracy.",
      results: [
        "True channel contribution revealed: brand search over-credited by 340%",
        "Budget reallocation to mid-funnel channels drove 22% revenue increase",
        "Executive dashboard gave CMO real-time marketing P&L visibility",
        "Incrementality testing framework now embedded in quarterly planning",
      ],
      duration: "5 months",
      tools: ["GA4", "Python", "BigQuery", "Looker Studio", "Meta Ads API"],
    },
  },
];
